/**
 * RDO CONSOLIDADO — modelo do documento.
 *
 * Um RDO Consolidado corresponde a 1 PROJETO + 1 FRENTE DE SERVIÇO + 1
 * INTERVALO DE DATAS. Este módulo reúne os apontamentos existentes conforme
 * esses filtros e os organiza; ele não resume, não funde e não reescreve nada:
 * cada apontamento original continua sendo um item individual, com a descrição
 * exatamente como o usuário salvou.
 *
 * O mesmo modelo alimenta o preview e o PDF — é o que garante que a sequência
 * Frente → Colaborador → Data → Registro seja idêntica nos dois.
 */

import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import type { EnrichedRdo } from "./rdo-domain.js";

/**
 * Escopo de colaborador do documento. Só existem dois — ou o RDO é de TODOS
 * os colaboradores da Frente no período, ou é de UM colaborador. Não há
 * seleção múltipla: um documento com dois ou três escolhidos a dedo não
 * corresponde a nada no fluxo documental.
 */
export type CollaboratorScope = "ALL" | "ONE";

export interface RdoConsolidationFilters {
  projectId: string;
  serviceFrontId: string;
  startDate: string;
  endDate: string;
  /** Omitido vale TODOS — o padrão do modal. */
  collaboratorScope?: CollaboratorScope;
  /** Obrigatório quando o escopo é UM COLABORADOR. */
  professionalId?: string;
}

export interface ConsolidatedRecord {
  rdoEntryId: string;
  date: string;
  startTime: string;
  endTime: string;
  overtimeHours: number;
  normalHours: number;
  om: string;
  functionName: string;
  shift: string;
  workOrderCode: string;
  /** Texto exatamente como salvo pelo usuário — nunca reescrito. */
  description: string;
  inspectorComment: string;
}

export interface ConsolidatedDay {
  date: string;
  records: ConsolidatedRecord[];
}

export interface ConsolidatedCollaborator {
  professionalId: string;
  name: string;
  functionName: string;
  days: ConsolidatedDay[];
  /** Todos os registros do colaborador, já na ordem final. */
  records: ConsolidatedRecord[];
  totalDays: number;
  totalNormalHours: number;
  totalOvertimeHours: number;
}

export interface RdoConsolidatedHeader {
  emissionDate: string;
  projectName: string;
  serviceFrontName: string;
  periodStart: string;
  periodEnd: string;
  clientName: string;
  clientDocument: string;
  clientAcronym: string;
  documentNumber: string;
  documentRevision: string;
}

export interface RdoConsolidatedModel {
  header: RdoConsolidatedHeader;
  collaborators: ConsolidatedCollaborator[];
  totalCollaborators: number;
  totalRecords: number;
  totalDays: number;
  totalNormalHours: number;
  totalOvertimeHours: number;
}

export const NO_ENTRIES_MESSAGE =
  "Nenhum apontamento encontrado para a Frente e período selecionados.";

export const CONSOLIDATION_REQUIRES_FRONT = "Selecione a Frente de Serviço do RDO Consolidado.";
export const CONSOLIDATION_REQUIRES_PROJECT = "Selecione o Projeto do RDO Consolidado.";
export const CONSOLIDATION_REQUIRES_START = "Informe a Data Inicial do período.";
export const CONSOLIDATION_REQUIRES_END = "Informe a Data Final do período.";
export const CONSOLIDATION_INVALID_PERIOD = "A Data Final não pode ser anterior à Data Inicial.";
export const CONSOLIDATION_REQUIRES_COLLABORATOR = "Selecione o colaborador do RDO Consolidado.";

export function validateConsolidationFilters(filters: RdoConsolidationFilters): void {
  if (!filters.projectId?.trim()) {
    throw new AppError(ErrorCode.Validation, "Consolidation requires a project", { safeMessage: CONSOLIDATION_REQUIRES_PROJECT });
  }
  if (!filters.serviceFrontId?.trim()) {
    throw new AppError(ErrorCode.Validation, "Consolidation requires a service front", { safeMessage: CONSOLIDATION_REQUIRES_FRONT });
  }
  if (!filters.startDate?.trim()) {
    throw new AppError(ErrorCode.Validation, "Consolidation requires a start date", { safeMessage: CONSOLIDATION_REQUIRES_START });
  }
  if (!filters.endDate?.trim()) {
    throw new AppError(ErrorCode.Validation, "Consolidation requires an end date", { safeMessage: CONSOLIDATION_REQUIRES_END });
  }
  if (filters.endDate < filters.startDate) {
    throw new AppError(ErrorCode.Validation, "Consolidation end date precedes start date", { safeMessage: CONSOLIDATION_INVALID_PERIOD });
  }
  if (filters.collaboratorScope === "ONE" && !filters.professionalId?.trim()) {
    throw new AppError(ErrorCode.Validation, "Consolidation scope ONE requires a professional", {
      safeMessage: CONSOLIDATION_REQUIRES_COLLABORATOR
    });
  }
}

/**
 * Apontamentos que entram no documento: da Frente escolhida, dentro do
 * período, dos colaboradores pedidos. Cada rdoEntryId entra uma única vez —
 * duplicatas vindas de uma listagem repetida são descartadas.
 */
export function filterEntriesForConsolidation(
  entries: readonly EnrichedRdo[],
  filters: RdoConsolidationFilters
): EnrichedRdo[] {
  // Um colaborador, ou todos. Nunca um subconjunto escolhido a dedo.
  const onlyProfessionalId =
    filters.collaboratorScope === "ONE" ? (filters.professionalId ?? "") : "";

  const seen = new Set<string>();
  const selected: EnrichedRdo[] = [];

  for (const entry of entries) {
    if (entry.serviceFrontId !== filters.serviceFrontId) continue;
    // A Frente já pertence a um único Projeto; quando o apontamento carrega o
    // Projeto, ele precisa ser o mesmo pedido — Projeto A nunca traz registro
    // preso ao Projeto B.
    if (entry.projectId && entry.projectId !== filters.projectId) continue;
    if (entry.date < filters.startDate || entry.date > filters.endDate) continue;
    if (entry.deletedAt) continue;
    if (entry.status === "CANCELLED") continue;
    if (onlyProfessionalId && entry.professionalId !== onlyProfessionalId) continue;
    if (seen.has(entry.id)) continue;

    seen.add(entry.id);
    selected.push(entry);
  }

  return selected;
}

const text = (value: unknown): string => {
  const raw = value === null || value === undefined ? "" : String(value);
  return raw.trim();
};

/** Campo ausente aparece como "—"; nada é preenchido por invenção. */
export const EMPTY_FIELD = "—";

function toRecord(entry: EnrichedRdo): ConsolidatedRecord {
  return {
    rdoEntryId: entry.id,
    date: entry.date,
    startTime: text(entry.startTime) || EMPTY_FIELD,
    endTime: text(entry.endTime) || EMPTY_FIELD,
    overtimeHours: Number(entry.overtimeHours) || 0,
    normalHours: Number(entry.normalHours) || 0,
    om: text(entry.om) || EMPTY_FIELD,
    functionName: text(entry.functionName) || EMPTY_FIELD,
    shift: text(entry.shift) || EMPTY_FIELD,
    workOrderCode: text(entry.workOrderCode) || EMPTY_FIELD,
    // Exatamente o que o usuário salvou. Sem IA, sem junção, sem reescrita.
    description: text(entry.description),
    inspectorComment: text((entry as any).inspectorComment)
  };
}

/**
 * Ordem determinística exigida pelo documento:
 *   colaborador (nome) ASC → data ASC → sequência do apontamento ASC.
 * O desempate final é o id, para que duas execuções devolvam sempre a mesma
 * ordem mesmo com createdAt idêntico.
 */
export function compareEntriesForConsolidation(a: EnrichedRdo, b: EnrichedRdo): number {
  const nameA = a.professionalName || a.professionalId;
  const nameB = b.professionalName || b.professionalId;
  const byName = nameA.localeCompare(nameB, "pt-BR", { sensitivity: "base" });
  if (byName !== 0) return byName;

  const byDate = a.date.localeCompare(b.date);
  if (byDate !== 0) return byDate;

  const seqA = (a as any).sequence;
  const seqB = (b as any).sequence;
  if (typeof seqA === "number" && typeof seqB === "number" && seqA !== seqB) return seqA - seqB;

  const byCreation = String(a.createdAt ?? "").localeCompare(String(b.createdAt ?? ""));
  if (byCreation !== 0) return byCreation;

  return a.id.localeCompare(b.id);
}

const round2 = (value: number) => Number(value.toFixed(2));

/**
 * Monta o modelo do documento. Um bloco por colaborador; dentro dele, um
 * separador por data; dentro da data, cada apontamento como registro próprio.
 * Três apontamentos do mesmo colaborador no mesmo dia continuam sendo três.
 */
/**
 * Nome do arquivo do PDF, a partir do que define o documento.
 * Ex.: RDO_TS-01_15-09-2026_a_20-09-2026.pdf
 *      RDO_TS-01_ATILA-DA-SILVA_15-09-2026_a_20-09-2026.pdf
 */
export function buildConsolidatedFileName(input: {
  serviceFrontName: string;
  periodStart: string;
  periodEnd: string;
  collaboratorName?: string | null;
}): string {
  const sanitize = (value: string) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");

  const date = (iso: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? "");
    return match ? `${match[3]}-${match[2]}-${match[1]}` : sanitize(iso);
  };

  // A Frente costuma vir como "TS-01 — TUBOVIA 32"; o código basta no arquivo.
  const front = sanitize(String(input.serviceFrontName ?? "").split("—")[0] ?? "") || "FRENTE";
  const parts = ["RDO", front];
  if (input.collaboratorName) parts.push(sanitize(input.collaboratorName));
  parts.push(date(input.periodStart), "a", date(input.periodEnd));

  return `${parts.join("_")}.pdf`;
}

export function buildConsolidatedModel(
  entries: readonly EnrichedRdo[],
  header: RdoConsolidatedHeader
): RdoConsolidatedModel {
  const ordered = [...entries].sort(compareEntriesForConsolidation);

  const byCollaborator = new Map<string, ConsolidatedCollaborator>();

  for (const entry of ordered) {
    const key = entry.professionalId;
    let collaborator = byCollaborator.get(key);
    if (!collaborator) {
      collaborator = {
        professionalId: entry.professionalId,
        name: entry.professionalName || entry.professionalId,
        functionName: text(entry.functionName) || EMPTY_FIELD,
        days: [],
        records: [],
        totalDays: 0,
        totalNormalHours: 0,
        totalOvertimeHours: 0
      };
      byCollaborator.set(key, collaborator);
    }

    const record = toRecord(entry);
    collaborator.records.push(record);

    let day = collaborator.days[collaborator.days.length - 1];
    if (!day || day.date !== record.date) {
      day = { date: record.date, records: [] };
      collaborator.days.push(day);
    }
    day.records.push(record);

    collaborator.totalNormalHours += record.normalHours;
    collaborator.totalOvertimeHours += record.overtimeHours;
  }

  const collaborators = Array.from(byCollaborator.values())
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }))
    .map(collaborator => ({
      ...collaborator,
      totalDays: collaborator.days.length,
      totalNormalHours: round2(collaborator.totalNormalHours),
      totalOvertimeHours: round2(collaborator.totalOvertimeHours)
    }));

  const allDates = new Set<string>();
  let totalNormal = 0;
  let totalOvertime = 0;
  let totalRecords = 0;

  for (const collaborator of collaborators) {
    totalNormal += collaborator.totalNormalHours;
    totalOvertime += collaborator.totalOvertimeHours;
    totalRecords += collaborator.records.length;
    for (const day of collaborator.days) allDates.add(day.date);
  }

  return {
    header,
    collaborators,
    totalCollaborators: collaborators.length,
    totalRecords,
    totalDays: allDates.size,
    totalNormalHours: round2(totalNormal),
    totalOvertimeHours: round2(totalOvertime)
  };
}
